import React from "react";
import { Trash2, PlusCircle, Tag } from "react-feather";
import toast from "react-hot-toast";
import ProductPicker from "./ProductPicker";
import { useLazyGetLastPurchasePriceQuery } from "../stockApi";

// Reusable editable line-items table shared by every Stock document form
// (LPO, Stock-In, Stock-Out, Return, Adjustment). Each document only turns
// on the columns it actually has (see `columns` prop) - LPO is the only one
// with VAT, Adjustment is the only one with no rate/amount at all (qty-only
// per Batch42_StockManagement.sql, no monetary value on that document).
//
// A row's product can be locked (picked/prefilled elsewhere - an LPO line
// being received into Stock-In, a Stock-Out line being returned, a Vehicle
// Request spare item) so the operator can't accidentally swap the product
// out from under a cross-document reference.
export const emptyRow = () => ({
  key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  productCode: "",
  productName: "",
  unit: "",
  qty: "",
  rate: "",
  vatPercent: "",
  remarks: "",
  reason: "",
  maxQty: null,
  locked: false,
  refId: null,
});

export const computeLineAmount = (row, columns) => {
  const qty = Number(row.qty) || 0;
  const rate = columns.showRate ? Number(row.rate) || 0 : 0;
  const base = qty * rate;
  const vatAmount = columns.showVat ? (base * (Number(row.vatPercent) || 0)) / 100 : 0;
  return { base, vatAmount, lineTotal: base + vatAmount };
};

export const computeTotals = (rows, columns) =>
  rows.reduce(
    (acc, row) => {
      const { base, vatAmount, lineTotal } = computeLineAmount(row, columns);
      return {
        totalQty: acc.totalQty + (Number(row.qty) || 0),
        subTotal: acc.subTotal + base,
        vatAmount: acc.vatAmount + vatAmount,
        netAmount: acc.netAmount + lineTotal,
      };
    },
    { totalQty: 0, subTotal: 0, vatAmount: 0, netAmount: 0 }
  );

// One-click "fill from last purchase price" - GET /stock/last-purchase-price
// (Stock.ViewBalance) returns the most recent non-voided Stock-In's rate for
// this product, or all-nulls if it was never purchased (never an error).
// Used on Stock-Out only, where the issue price commonly mirrors what was
// last paid - not shown on documents that already have their own rate
// source (LPO lines are typed fresh, Stock-In prefills from the LPO itself).
const LastPurchasePriceButton = ({ branchId, productCode, onFill }) => {
  const [trigger, { isFetching }] = useLazyGetLastPurchasePriceQuery();

  const handleClick = async () => {
    const { data, error } = await trigger({ branchId, productCode });
    if (error || !data) return toast.error("Could not look up the last purchase price");
    if (data.unitRate == null) return toast.error("No prior purchase found for this product");
    onFill(data.unitRate);
    const date = data.stockInDate ? new Date(data.stockInDate).toLocaleDateString() : "";
    toast.success(
      `Last purchased at ${Number(data.unitRate).toFixed(2)} on ${date} (${data.stockInNo}, ${data.supplierName || "supplier unknown"})`
    );
  };

  return (
    <button
      type="button"
      className="btn btn-link p-0 mt-1 d-inline-flex align-items-center gap-1 small"
      onClick={handleClick}
      disabled={isFetching}
      title="Fill from last purchase price"
    >
      <Tag size={12} /> Last price
    </button>
  );
};

const LineItemsEditor = ({
  branchId,
  rows,
  onChange,
  columns = { showRate: true, showVat: false, showRemarks: false, showReason: false, showMaxQty: false, showLastPurchasePrice: false },
  disabled = false,
  defaultVatPercent = 0,
}) => {
  const updateRow = (key, patch) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () => onChange([...rows, { ...emptyRow(), vatPercent: defaultVatPercent }]);
  const removeRow = (key) => onChange(rows.filter((r) => r.key !== key));

  return (
    <div className="table-responsive">
      <table className="table table-bordered align-middle">
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            <th style={{ minWidth: 220 }}>Product</th>
            <th style={{ width: 90 }}>Unit</th>
            <th style={{ width: 110 }}>Qty{columns.showMaxQty ? " (max)" : ""}</th>
            {columns.showRate && <th style={{ width: 120 }}>Rate</th>}
            {columns.showVat && <th style={{ width: 90 }}>VAT %</th>}
            {columns.showVat && <th style={{ width: 110 }}>VAT Amt</th>}
            {columns.showRate && <th style={{ width: 120 }}>Amount</th>}
            {columns.showRemarks && <th style={{ minWidth: 150 }}>Remarks</th>}
            {columns.showReason && <th style={{ minWidth: 150 }}>Reason</th>}
            {!disabled && <th style={{ width: 50 }} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const { vatAmount, lineTotal } = computeLineAmount(row, columns);
            return (
              <tr key={row.key}>
                <td>{idx + 1}</td>
                <td>
                  {row.locked ? (
                    <span>{row.productName} <span className="text-muted">({row.productCode})</span></span>
                  ) : (
                    <ProductPicker
                      branchId={branchId}
                      disabled={disabled}
                      value={row.productCode ? { value: row.productCode, label: `${row.productName} (${row.productCode})` } : null}
                      onChange={(opt) =>
                        updateRow(row.key, {
                          productCode: opt?.value ?? "",
                          productName: opt?.product?.productName ?? "",
                          unit: opt?.product?.unit ?? row.unit,
                          rate: columns.showRate && !row.rate ? opt?.product?.salesRate ?? row.rate : row.rate,
                        })
                      }
                    />
                  )}
                </td>
                <td>
                  <input
                    type="text"
                    className="form-control"
                    value={row.unit || ""}
                    onChange={(e) => updateRow(row.key, { unit: e.target.value })}
                    disabled={disabled}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={row.qty}
                    onChange={(e) => updateRow(row.key, { qty: e.target.value })}
                    disabled={disabled}
                    max={row.maxQty ?? undefined}
                  />
                  {columns.showMaxQty && row.maxQty != null && (
                    <div className="text-muted small">of {row.maxQty}</div>
                  )}
                </td>
                {columns.showRate && (
                  <td>
                    <input
                      type="number"
                      className="form-control"
                      value={row.rate}
                      onChange={(e) => updateRow(row.key, { rate: e.target.value })}
                      disabled={disabled}
                    />
                    {columns.showLastPurchasePrice && !disabled && row.productCode && branchId && (
                      <LastPurchasePriceButton
                        branchId={branchId}
                        productCode={row.productCode}
                        onFill={(unitRate) => updateRow(row.key, { rate: unitRate })}
                      />
                    )}
                  </td>
                )}
                {columns.showVat && (
                  <td>
                    <input
                      type="number"
                      className="form-control"
                      value={row.vatPercent}
                      onChange={(e) => updateRow(row.key, { vatPercent: e.target.value })}
                      disabled={disabled}
                    />
                  </td>
                )}
                {columns.showVat && <td className="text-end">{vatAmount.toFixed(2)}</td>}
                {columns.showRate && <td className="text-end">{lineTotal.toFixed(2)}</td>}
                {columns.showRemarks && (
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={row.remarks || ""}
                      onChange={(e) => updateRow(row.key, { remarks: e.target.value })}
                      disabled={disabled}
                    />
                  </td>
                )}
                {columns.showReason && (
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={row.reason || ""}
                      onChange={(e) => updateRow(row.key, { reason: e.target.value })}
                      disabled={disabled}
                    />
                  </td>
                )}
                {!disabled && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-link text-danger p-1"
                      onClick={() => removeRow(row.key)}
                      title="Remove line"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={10} className="text-center text-muted py-3">
                No lines added yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {!disabled && (
        <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" onClick={addRow}>
          <PlusCircle size={14} /> Add Line
        </button>
      )}
    </div>
  );
};

export default LineItemsEditor;
